import {
    Identifier,
    LanguageProduct,
    Product,
    ProductLanguages,
    PurchasedProduct,
    Text,
    TranslatedProduct,
} from "../types/PersologOnlineTypes.ts";
import { LanguageManagementAPI, ProfileAdministrationAPI } from "../api";
import { i18n, keycloak } from "../main.ts";
import { getImpersonatedAccount } from "./GeneralUtils.ts";
import { BENCHMARK_JOBMATCH, JOB_PROFILE, JOB_PROFILE_SHORT } from "./ProjectUtils.ts";

/**
 * Resolves the trainer whose products should be loaded: the impersonated
 * (associated) account when one is active, otherwise the logged-in trainer.
 */
function resolveTrainerId(): string {
    return getImpersonatedAccount()?.associatedAccount?.id ?? <string>keycloak.tokenParsed?.sub;
}

export async function fetchData(): Promise<{
    purchased: TranslatedProduct[];
    all: TranslatedProduct[];
}> {
    const [purchasedProducts, allProducts, productIdentifiers, modelIdentifiers, profileLanguages] = await Promise.all([
        ProfileAdministrationAPI.fetchPurchasedProducts(
            resolveTrainerId(),
        ),
        fetchAllProductsForTrainer(
            resolveTrainerId(),
        ),
        LanguageManagementAPI.fetchTexts("products"),
        LanguageManagementAPI.fetchTexts("models"),
        LanguageManagementAPI.fetchLanguages(),
    ]);

    const profileLanguagesMap = extractLanguages(profileLanguages);

    const mappedAllProducts: TranslatedProduct[] = allProducts.map((product: Product) => {
        const productIdentifier = productIdentifiers.find((id: Identifier) => id.identifier === product.productNo);
        const productTextObj =
            productIdentifier?.texts.find((t: Text) => t.language === i18n.global.locale.value) ??
            productIdentifier?.texts.find((t: Text) => t.language === "en") ??
            productIdentifier?.texts.find((t: Text) => t.language === "de");
        const productName = productTextObj?.text || product.productNo;
        const modelIdentifier = modelIdentifiers.find((id: Identifier) => id.identifier === product?.model.model);
        const modelTextObj =
            modelIdentifier?.texts.find((t: Text) => t.language === i18n.global.locale.value) ??
            modelIdentifier?.texts.find((t: Text) => t.language === "en");
        const modelName = modelTextObj?.text || product?.model.model;
        const profileLanguages = profileLanguagesMap[product.productNo];
        const questionnaireLanguages = profileLanguagesMap[`questionnaire-${product.productNo}`];

        return {
            productName,
            productNo: product.productNo,
            quantity: 0,
            shopLink: product.shopLink,
            modelName: modelName,
            modelNo: product.model.model,
            rank: product.model.rank,
            profileLanguages: profileLanguages,
            questionnaireLanguages: questionnaireLanguages,
        };
    });

    const mappedPurchasedProduct: TranslatedProduct[] = purchasedProducts.map((purchasedProduct: PurchasedProduct) => {
        const product = allProducts.find((p: Product) => p.productNo === purchasedProduct.product);
        const productIdentifier = productIdentifiers.find(
            (id: Identifier) => id.identifier === purchasedProduct.product,
        );
        const productTextObj =
            productIdentifier?.texts.find((t: Text) => t.language === i18n.global.locale.value) ??
            productIdentifier?.texts.find((t: Text) => t.language === "en") ??
            productIdentifier?.texts.find((t: Text) => t.language === "de");
        const productName = productTextObj?.text || purchasedProduct.product;
        const modelIdentifier = modelIdentifiers.find((id: Identifier) => id.identifier === product?.model.model);
        const modelTextObj =
            modelIdentifier?.texts.find((t: Text) => t.language === i18n.global.locale.value) ??
            modelIdentifier?.texts.find((t: Text) => t.language === "en");
        const modelName = modelTextObj?.text || product?.model.model || "";
        const profileLanguages = profileLanguagesMap[purchasedProduct.product];
        const questionnaireLanguages = profileLanguagesMap[`questionnaire-${purchasedProduct.product}`];

        return {
            productName,
            productNo: product?.productNo || "",
            quantity: purchasedProduct.quantity,
            shopLink: product?.shopLink || "",
            modelName: modelName,
            modelNo: product?.model?.model || "",
            rank: product?.model.rank || 0,
            profileLanguages: profileLanguages,
            questionnaireLanguages: questionnaireLanguages,
        };
    });

    const filterActivatedProducts = (products: TranslatedProduct[]): TranslatedProduct[] =>
        products.filter((product) => product.profileLanguages?.length > 0);

    return {
        purchased: filterActivatedProducts(mappedPurchasedProduct),
        all: filterActivatedProducts(mappedAllProducts),
    };
}

/**
 * Ensures the job-match comparison report (in122) is favorited once the trainer has a
 * job profile (in102) or short job profile (in116) available — whether favorited or purchased.
 *
 * A trainer can obtain a job profile in several ways, so rather than hooking each path we
 * check the stock the caller has already loaded (no extra backend call). The favorite endpoint
 * is a toggle and a favorited product is returned in that stock list (with quantity 0 when not
 * bought), so we only call it when in122 is not already present — otherwise we would un-favorite
 * it. in102/in116/in122 are language-activated, so they appear in the (filtered) loaded list.
 *
 * Best-effort: never throws, so a failure here cannot block the product view from loading.
 *
 * @param purchased the trainer's already-loaded stock (favorited + purchased products)
 * @returns true if in122 was newly favorited (caller should refresh product data).
 */
export async function ensureBenchmarkReportFavorited(purchased: TranslatedProduct[]): Promise<boolean> {
    const inStock = (productNo: string) => purchased.some((product) => product.productNo === productNo);

    if (inStock(BENCHMARK_JOBMATCH)) return false; // already favorited
    if (!inStock(JOB_PROFILE) && !inStock(JOB_PROFILE_SHORT)) return false; // no job profile yet

    try {
        await ProfileAdministrationAPI.favoriteProduct(resolveTrainerId(), BENCHMARK_JOBMATCH);
        return true;
    } catch (error) {
        console.error("Failed to auto-favorite the comparison report (in122):", error);
        return false;
    }
}

/**
 * Fetch all purchaseable products for that trainer (includes distributor specific products)
 * @param trainerId
 * @returns Promise<Product[]> - all products for that trainer
 */
export async function fetchAllProductsForTrainer(trainerId: string): Promise<Product[]> {
    return await ProfileAdministrationAPI.fetchAllProductsForTrainer(trainerId);
}

/**
 * Updates the purchased products and sorts them by productNo
 * @returns Promise<TranslatedProduct[]> - sorted purchased products
 */
export async function updateProducts() {
    const { purchased } = await fetchData();
    return purchased.sort((productA, productB) =>
        productA.productNo.localeCompare(productB.productNo, undefined, { numeric: true }),
    );
}

export function filterNotPurchased(all: TranslatedProduct[], purchased: TranslatedProduct[]): TranslatedProduct[] {
    const purchasedProductNos = new Set(purchased.map((p) => p.productNo));
    return all.filter((product) => !purchasedProductNos.has(product.productNo));
}

export function groupAndSort(products: TranslatedProduct[]): {
    [key: string]: TranslatedProduct[];
} {
    const groupedByModel = products.reduce<{
        [key: string]: TranslatedProduct[];
    }>((acc, product) => {
        if (!acc[product.modelName]) {
            acc[product.modelName] = [];
        }
        acc[product.modelName].push(product);
        return acc;
    }, {});

    const orderedModels = getOrderedModels(products);
    return orderedModels.reduce<{ [key: string]: TranslatedProduct[] }>((acc, model) => {
        acc[model] = (groupedByModel[model] || []).sort((a: TranslatedProduct, b: TranslatedProduct) => {
            if (a.productNo < b.productNo) return -1;
            if (a.productNo > b.productNo) return 1;
            return 0;
        });
        return acc;
    }, {});
}

function getOrderedModels(products: TranslatedProduct[]): string[] {
    return Array.from(new Set(products.map((p) => p.modelName))).sort(
        (modelA, modelB) =>
            (products.find((p) => p.modelName === modelA)?.rank || 0) -
            (products.find((p) => p.modelName === modelB)?.rank || 0),
    );
}

export function extractLanguages(productLanguages: LanguageProduct[]): ProductLanguages {
    return productLanguages.reduce<ProductLanguages>((acc, languageProduct) => {
        acc[languageProduct.name] = languageProduct.languages.map((lang: string) => lang);
        return acc;
    }, {});
}
